import { useState, useEffect } from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import plusButton from '../../../images/icons/plus-icon.png'
import IconButton from '../IconButton'
import deleteIcon from '../../../images/icons/delete-icon.png'
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined'
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined'
import Tooltip from '../Tooltip'

/**
 * De antwoordopties van een meerkeuzeveld: toevoegen, hernoemen, verslepen,
 * verwijderen.
 *
 * Losgetrokken uit `DataModalContent` zodat de gegevenstabel dezelfde editor
 * gebruikt in plaats van een tweede kopie ervan — twee kopieën lopen uiteen, en
 * dan slaat hetzelfde veldtype op twee schermen iets anders op.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @param {object} props
 * @param {Array<{docid: string, Option: string}>} [props.options] - Opties, al op volgorde
 * @param {() => void} props.addOption - Voegt een lege optie toe
 * @param {(docid: string, value: string) => void} props.onChangeOption - Hernoemt een optie
 * @param {(docid: string) => void} props.onDeleteOption - Verwijdert een optie
 * @param {(items: Array<object>) => void} [props.onReorderOptions] - Krijgt de nieuwe volgorde
 * @param {boolean} [props.canEdit] - Of de gebruiker mag aanpassen
 * @returns {JSX.Element}
 */
const DataFieldOptions = ({
  options = [],
  addOption,
  onChangeOption,
  onDeleteOption,
  onReorderOptions,
  canEdit = true,
}) => {
  const [optionsDrag, setOptionsDrag] = useState([])

  useEffect(() => {
    setOptionsDrag(options || [])
  }, [options])

  const handleDragEnd = async (result) => {
    if (!result.destination) return

    const items = Array.from(optionsDrag)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)

    setOptionsDrag(items)

    if (onReorderOptions) {
      await onReorderOptions(items)
    }
  }

  // Pas wegschrijven als het veld de aandacht verliest, niet per toetsaanslag.
  // Een gestuurd invoerveld dat per aanslag naar Firestore schrijft springt
  // terug op wat de snapshot teruggeeft: van 'Geaccepteerd' blijft dan alleen
  // de laatste letter over.
  // AI-CONTEXT: See docs/pitfalls.md#ui-tabletitlecell-onchange-refocus
  const handleBlurOption = (e, option) => {
    const nieuw = e.target.value
    if (!onChangeOption) return
    if (nieuw === (option.Option || '')) return
    onChangeOption(option.docid, nieuw)
  }

  const handleDeleteOption = (docid) => {
    if (onDeleteOption) onDeleteOption(docid)
  }

  return (
        <div>
          <h3>Meerkeuze opties</h3>
          <p>Voeg de opties toe voor het meerkeuze item, één per regel</p>

          <div className="textarea-multiple-options-container">
            {canEdit && (
              <Tooltip
                content={'Antwoordoptie toevoegen'}
                width={'150px'}
                left="-10px"
                top="-75px"
              >
                <IconButton
                  icon={plusButton}
                  label="Antwoordoptie toevoegen"
                  onClick={addOption}
                  imgClassName="add-data-options-button"
                />
              </Tooltip>
            )}

            <div className="textarea-multiple-options-list">
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="data-options" direction="vertical">
                  {(provided) => (
                    <div
                      id="options-container"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {optionsDrag &&
                        optionsDrag.map((option, index) => (
                          <Draggable
                            key={option.docid}
                            draggableId={option.docid}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                className="multiple-option-row"
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <span
                                  className="drag-handle"
                                  {...provided.dragHandleProps}
                                >
                                  <DragIndicatorOutlinedIcon />
                                </span>

                                <CheckBoxOutlineBlankOutlinedIcon />

                                <input
                                  className="question-checkbox-input"
                                  placeholder="Schrijf hier de antwoordoptie"
                                  defaultValue={option.Option || ''}
                                  onBlur={(e) => handleBlurOption(e, option)}
                                />

                                {canEdit && (
                                  <img
                                    className="muitple-delete-icon"
                                    onClick={() => handleDeleteOption(option.docid)}
                                    src={deleteIcon}
                                    alt="Verwijderen"
                                  />
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        </div>
  )
}

export default DataFieldOptions
